import { lazy, Suspense } from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";

import { SiteLayout } from "@/components/layout/site-layout";
import { PageSkeleton } from "@/components/page-state";

// Route-level code splitting keeps the home LCP budget intact (AC-8).
const HomePage = lazy(() => import("@/features/home/home-page"));
const VillaPage = lazy(() => import("@/features/villa/villa-page"));
const StoryPage = lazy(() => import("@/features/story/story-page"));
const ExperiencesPage = lazy(() => import("@/features/experiences/experiences-page"));
const GalleryPage = lazy(() => import("@/features/gallery/gallery-page"));
const BookingPage = lazy(() => import("@/features/booking/booking-page"));
const ThankYouPage = lazy(() => import("@/features/booking/thank-you-page"));
const ContactPage = lazy(() => import("@/features/contact/contact-page"));
const FaqPage = lazy(() => import("@/features/faq/faq-page"));
const LegalPage = lazy(() => import("@/features/legal/legal-page"));
const NotFoundPage = lazy(() => import("@/features/site/not-found-page"));

const AdminLayout = lazy(() =>
  import("@/features/admin/admin-layout").then((m) => ({ default: m.AdminLayout })),
);
const AdminPagesPage = lazy(() => import("@/features/admin/pages/admin-pages-page"));
const AdminEditorPage = lazy(() => import("@/features/admin/pages/admin-editor-page"));
const AdminMediaPage = lazy(() => import("@/features/admin/pages/admin-media-page"));
const AdminBookingsPage = lazy(() => import("@/features/admin/pages/admin-bookings-page"));
const AdminSettingsPage = lazy(() => import("@/features/admin/pages/admin-settings-page"));

function withSuspense(element: React.ReactNode) {
  return <Suspense fallback={<PageSkeleton />}>{element}</Suspense>;
}

const router = createBrowserRouter([
  {
    element: <SiteLayout />,
    children: [
      { index: true, element: withSuspense(<HomePage />) },
      { path: "villa", element: withSuspense(<VillaPage />) },
      { path: "story", element: withSuspense(<StoryPage />) },
      { path: "experiences", element: withSuspense(<ExperiencesPage />) },
      { path: "gallery", element: withSuspense(<GalleryPage />) },
      { path: "booking", element: withSuspense(<BookingPage />) },
      { path: "thank-you", element: withSuspense(<ThankYouPage />) },
      { path: "contact", element: withSuspense(<ContactPage />) },
      { path: "faq", element: withSuspense(<FaqPage />) },
      { path: "legal/:slug", element: withSuspense(<LegalPage />) },
      { path: "*", element: withSuspense(<NotFoundPage />) },
    ],
  },
  {
    path: "admin",
    element: withSuspense(<AdminLayout />),
    children: [
      { index: true, element: withSuspense(<AdminPagesPage />) },
      { path: "editor", element: withSuspense(<AdminEditorPage />) },
      { path: "media", element: withSuspense(<AdminMediaPage />) },
      { path: "bookings", element: withSuspense(<AdminBookingsPage />) },
      { path: "settings", element: withSuspense(<AdminSettingsPage />) },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
